import { Link } from "react-router";
import { LogIn, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { useUi } from "@/lib/store/ui";
import { useSession } from "@/lib/store/session";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Brand } from "./Brand";
import { NavList } from "./NavList";
import { primaryNav, secondaryNav } from "./nav";

export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_RAIL_WIDTH = 64;

export function Sidebar() {
  const collapsed = useUi((s) => s.sidebarCollapsed);
  const toggle = useUi((s) => s.toggleSidebar);
  const loggedIn = useSession((s) => !!s.userId);
  const primary = loggedIn ? primaryNav : primaryNav.filter((i) => i.public);
  const secondary = loggedIn ? secondaryNav : secondaryNav.filter((i) => i.public);

  return (
    <aside
      className={cn("fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-border bg-surface pt-safe-top transition-[width] duration-200 tab:flex")}
      style={{ width: collapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_WIDTH }}
      aria-label="Sidebar"
    >
      <div className={cn("flex h-16 items-center px-4", collapsed && "justify-center px-0")}>
        <Brand collapsed={collapsed} to={loggedIn ? "/home" : "/auth"} />
      </div>
      <nav className="scrollbar-thin flex flex-1 flex-col gap-4 overflow-y-auto px-3 py-2" aria-label="Main">
        <NavList items={primary} collapsed={collapsed} />
        <Separator />
        <NavList items={secondary} collapsed={collapsed} />
      </nav>
      <div className={cn("flex flex-col gap-2 border-t border-border p-3", collapsed && "items-center")}>
        {!loggedIn &&
          (collapsed ? (
            <SimpleTooltip label="Sign in" side="right">
              <Button asChild size="icon" aria-label="Sign in">
                <Link to="/auth">
                  <LogIn />
                </Link>
              </Button>
            </SimpleTooltip>
          ) : (
            <Button asChild className="w-full">
              <Link to="/auth">
                <LogIn />
                Sign in
              </Link>
            </Button>
          ))}
        <SimpleTooltip label={collapsed ? "Expand sidebar" : "Collapse sidebar"} side="right">
          <Button
            variant="ghost"
            size={collapsed ? "icon" : "sm"}
            onClick={toggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            className={cn("text-fg-muted", !collapsed && "w-full justify-start")}
          >
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            {!collapsed && "Collapse"}
          </Button>
        </SimpleTooltip>
      </div>
    </aside>
  );
}
